import React, { useCallback, useEffect, useRef, useState } from "react";
import { navigate } from "@reach/router";
import { useCode, useUser } from "../../hooks/useApi";
import { requiresTask } from "../../helpers/taskGates";
import {
  clearEntryIntent,
  clearLegacyRedirectLogin,
  getEntryIntent,
  getEntryIntentTargetPath,
  patchEntryIntent,
  saveEntryIntent,
  setLegacyRedirectLogin,
  buildEntryIntent,
  isSameEntryTarget,
} from "./entryIntentStorage";
import {
  parseRedeemCodeParam,
  resolveEntryTarget,
} from "./entryIntentResolver";
import EntryFlowGate from "./EntryFlowGate";
import { useEntryBrandingRegistration } from "./EntryBranding";
import useEntryTerms from "./useEntryTerms";
import EntryLoginGate from "./EntryLoginGate";
import Sonicbox from "../../components/screens/sonicbox";
import TaskContainer from "../../components/screens/tasks";
import {
  clearPendingRedeemContext,
  setPendingRedeemContext,
} from "../../services/eventEmitter/storage";

const buildOriginalPath = (location) => {
  if (!location) return "";
  return `${location.pathname ?? ""}${location.search ?? ""}${location.hash ?? ""}`;
};

export default function EntryFlowController({
  source,
  nameseo,
  code,
  location,
}) {
  const originalPath = buildOriginalPath(location);
  const entryKey = `${source}:${nameseo || ""}:${code || ""}:${originalPath}`;
  const currentEntryKey = useRef(entryKey);
  currentEntryKey.current = entryKey;
  const { logged, initiated, setLoginRedirect, updateUser, user } = useUser();
  const { sendCode } = useCode();
  const [intent, setIntent] = useState(null);
  const [resolvedTarget, setResolvedTarget] = useState({
    entryKey: null,
    isLoading: true,
    error: null,
    nameseo: source === "redeem" ? null : nameseo,
    radioid: null,
    redeem: null,
  });
  const [redeemState, setRedeemState] = useState({
    status: "idle",
    error: null,
  });
  const sentRedeemRef = useRef(null);

  const encodedRedeemCode =
    source === "redeem" ? parseRedeemCodeParam(code) : null;

  useEffect(() => {
    const existingIntent = getEntryIntent();
    if (
      source === "task" &&
      existingIntent?.redeem?.encodedCode &&
      existingIntent?.redeem?.status === "pending" &&
      existingIntent?.target?.nameseo === nameseo
    ) {
      setIntent(existingIntent);
      return;
    }

    const nextIntent = buildEntryIntent({
      source,
      originalPath,
      nameseo,
      encodedCode: encodedRedeemCode,
    });
    if (existingIntent && isSameEntryTarget(existingIntent, nextIntent)) {
      setIntent(existingIntent);
    } else {
      clearEntryIntent();
      saveEntryIntent(nextIntent);
      setIntent(nextIntent);
    }
  }, [source, originalPath, nameseo, encodedRedeemCode, logged]);

  useEffect(() => {
    let cancelled = false;
    setRedeemState({ status: "idle", error: null });
    sentRedeemRef.current = null;

    setResolvedTarget((current) => ({
      ...current,
      isLoading: true,
      error: null,
    }));

    resolveEntryTarget({ source, nameseo, code })
      .then((target) => {
        if (cancelled) return;

        setResolvedTarget({
          entryKey,
          isLoading: false,
          error: null,
          nameseo: target.nameseo,
          radioid: target.radioid,
          redeem: target.redeem,
        });

        patchEntryIntent({
          target: {
            nameseo: target.nameseo,
            radioid: target.radioid,
          },
          redeem: target.redeem
            ? {
                encodedCode: target.redeem.encodedCode,
                code: target.redeem.code,
                status: "pending",
              }
            : undefined,
        });
      })
      .catch((error) => {
        if (cancelled) return;
        setResolvedTarget({
          entryKey,
          isLoading: false,
          error,
          nameseo: null,
          radioid: null,
          redeem: null,
        });
      });

    return () => {
      cancelled = true;
    };
  }, [source, nameseo, code, entryKey]);

  useEffect(() => {
    const targetNameseo = resolvedTarget.nameseo;
    if (!targetNameseo && source !== "redeem") return;

    const redirectPath =
      source === "redeem" && encodedRedeemCode
        ? `/redeem/${encodedRedeemCode}`
        : `/sonicbox/${targetNameseo}`;

    setLegacyRedirectLogin(redirectPath);
    setLoginRedirect(redirectPath);
  }, [source, encodedRedeemCode, resolvedTarget.nameseo, setLoginRedirect]);

  useEffect(() => {
    const redeem = resolvedTarget.redeem;
    if (source !== "redeem" || !redeem?.encodedCode) return;

    setPendingRedeemContext({
      encodedCode: redeem.encodedCode,
      source: "qr",
      sourceDetail: "redeem_route",
      capturedAt: Date.now(),
      radioHint: {
        radioid: redeem.radioid,
        nameseo: redeem.nameseo,
      },
    });
  }, [source, resolvedTarget.redeem]);

  const terms = useEntryTerms({
    nameseo: resolvedTarget.nameseo,
    intent,
    enabled:
      initiated &&
      resolvedTarget.entryKey === entryKey &&
      !resolvedTarget.isLoading &&
      !resolvedTarget.error,
    continuation: location?.state?.termsContinuation,
    onIntentChange: setIntent,
  });
  const { ready: termsReady, getContinuation } = terms;

  useEffect(() => {
    const redeem = resolvedTarget.redeem;
    const taskRequiredBeforeRedeem =
      source === "redeem" &&
      redeem?.nameseo &&
      requiresTask(redeem.nameseo, user);
    if (
      source !== "redeem" ||
      !logged ||
      !termsReady ||
      taskRequiredBeforeRedeem ||
      !redeem?.code ||
      !redeem?.encodedCode ||
      !redeem?.nameseo ||
      redeemState.status !== "idle" ||
      sentRedeemRef.current === redeem.encodedCode
    ) {
      return;
    }

    sentRedeemRef.current = redeem.encodedCode;
    setRedeemState({ status: "loading", error: null });

    sendCode(
      {
        ccode: redeem.code,
        encodedCode: redeem.encodedCode,
        source: "qr",
        radio: {
          radioid: redeem.radioid,
          nameseo: redeem.nameseo,
        },
      },
      () => {
        if (currentEntryKey.current !== entryKey) return;
        updateUser();
        clearPendingRedeemContext();
        clearEntryIntent();
        clearLegacyRedirectLogin();
        setRedeemState({ status: "success", error: null });
        navigate(`/sonicbox/${redeem.nameseo}`, {
          replace: true,
          state: { termsContinuation: getContinuation() },
        });
      },
      (error) => {
        if (currentEntryKey.current !== entryKey) return;
        clearPendingRedeemContext();
        setRedeemState({ status: "error", error });
      },
    );
  }, [
    source,
    logged,
    resolvedTarget.redeem,
    redeemState.status,
    sendCode,
    updateUser,
    user,
    termsReady,
    getContinuation,
    entryKey,
  ]);

  const targetPath =
    getEntryIntentTargetPath(intent) ??
    (resolvedTarget.nameseo ? `/sonicbox/${resolvedTarget.nameseo}` : null);
  const targetNameseo = resolvedTarget.nameseo ?? nameseo;
  const shouldRunTask =
    targetNameseo && (source === "task" || requiresTask(targetNameseo, user));
  const shouldRunTaskBeforeRedeem =
    source === "redeem" && targetNameseo && requiresTask(targetNameseo, user);

  useEntryBrandingRegistration({
    pathname: location?.pathname,
    nameseo: resolvedTarget.entryKey === entryKey ? resolvedTarget.nameseo : source === "redeem" ? null : nameseo,
    active: !(source === "sonicbox" && logged && termsReady && !shouldRunTask && !resolvedTarget.error && resolvedTarget.entryKey === entryKey),
  });

  const handleTaskCompleted = useCallback(
    async ({ path }) => {
      if (intent?.redeem?.encodedCode && intent?.redeem?.status === "pending") {
        navigate(`/redeem/${intent.redeem.encodedCode}`, {
          replace: true,
          state: { termsContinuation: getContinuation() },
        });
        return;
      }

      if (path && targetNameseo) {
        const fromEntry = currentEntryKey.current;
        try {
          const url = new URL(path, window.location.origin);
          const hosts = [
            window.location.hostname,
            "millsonicbox.com",
            "www.millsonicbox.com",
            "sonicboxmusic.com",
            "www.sonicboxmusic.com",
          ];
          const sameRadioPath = `/sonicbox/${targetNameseo}`;
          let allowed =
            hosts.includes(url.hostname) && url.pathname === sameRadioPath;
          const redeemMatch =
            hosts.includes(url.hostname) &&
            url.pathname.match(/^\/redeem\/([^/]+)$/);
          if (redeemMatch) {
            const target = await resolveEntryTarget({
              source: "redeem",
              code: redeemMatch[1],
            });
            allowed = target.nameseo === targetNameseo;
          }
          if (currentEntryKey.current !== fromEntry) return;
          if (allowed) {
            navigate(`${url.pathname}${url.search}${url.hash}`, {
              replace: true,
              state: { termsContinuation: getContinuation() },
            });
            return;
          }
        } catch (_) {
          /* Unsupported task targets return to this same radio. */
        }
      }

      if (targetNameseo) {
        navigate(`/sonicbox/${targetNameseo}`, {
          replace: true,
          state: { termsContinuation: getContinuation() },
        });
      }
    },
    [intent, targetNameseo, getContinuation],
  );

  useEffect(() => {
    if (
      initiated &&
      logged &&
      termsReady &&
      source !== "redeem" &&
      targetNameseo &&
      !shouldRunTask
    ) {
      clearEntryIntent();
      clearLegacyRedirectLogin();
    }
  }, [initiated, logged, source, targetNameseo, shouldRunTask, termsReady]);

  useEffect(() => {
    if (
      initiated &&
      logged &&
      termsReady &&
      source === "sonicbox" &&
      shouldRunTask
    ) {
      navigate(`/task/${targetNameseo}`, {
        replace: true,
        state: { termsContinuation: getContinuation() },
      });
    }
  }, [
    initiated,
    logged,
    source,
    shouldRunTask,
    targetNameseo,
    termsReady,
    getContinuation,
  ]);

  useEffect(() => {
    if (initiated && logged && termsReady && shouldRunTaskBeforeRedeem) {
      navigate(`/task/${targetNameseo}`, {
        replace: true,
        state: { termsContinuation: getContinuation() },
      });
    }
  }, [
    initiated,
    logged,
    shouldRunTaskBeforeRedeem,
    targetNameseo,
    termsReady,
    getContinuation,
  ]);

  if (
    !initiated ||
    resolvedTarget.isLoading ||
    resolvedTarget.entryKey !== entryKey
  ) {
    return <EntryFlowGate title="home.welcome" />;
  }

  if (resolvedTarget.error) {
    return (
      <EntryFlowGate error={resolvedTarget.error} targetPath={targetPath} />
    );
  }

  if (terms.screen) return terms.screen;
  if (terms.loading) return <EntryFlowGate title="home.welcome" />;
  if (!logged)
    return terms.loginAllowed ? <EntryLoginGate /> : <EntryFlowGate />;
  if (!termsReady) return <EntryFlowGate />;

  if (source === "redeem") {
    if (redeemState.status === "error") {
      return (
        <EntryFlowGate
          error={redeemState.error ?? new Error("Redeem error")}
          targetPath={targetPath}
        />
      );
    }

    return <EntryFlowGate title="home.redeemtitle" />;
  }

  if (source === "sonicbox" && shouldRunTask) {
    return <EntryFlowGate title="home.welcome" />;
  }

  if (shouldRunTask) {
    return (
      <TaskContainer
        nameseo={targetNameseo}
        onCompleted={handleTaskCompleted}
      />
    );
  }

  if (targetNameseo) {
    return <Sonicbox nameseo={targetNameseo} termsManaged={true} />;
  }

  return <EntryFlowGate error={new Error("Radio not found")} />;
}
